import { APIProvider } from "@vis.gl/react-google-maps";
import { ReactElement, createContext, useContext, useEffect, useState } from "react";

import { useSendError } from "@/requests/report.mutations";
import { DriverType } from "@/types/driver.types";
import { IPlanPassenger, Task } from "@/types/tasks.types";
import { WeTrackVehicleData } from "@/types/wetrack.types";

export type RouteType = {
    task: Task<IPlanPassenger<"api">>;
    vehicle: WeTrackVehicleData;
    driver: DriverType | null;
};

const context = createContext<{
    location: { lat: number; lng: number } | undefined;
    setLocation: (location: { lat: number; lng: number }) => void;
    showRoute?: RouteType;
    setShowRoute: (route: RouteType | undefined) => void;
    mapLoaded: boolean;
    selectedVehicle: WeTrackVehicleData | undefined;
    setSelectedVehicle: (vehicle: WeTrackVehicleData | undefined) => void;
} | null>(null);

export const MapContextProvider = ({ children }: { children: ReactElement }) => {
    const [location, setLocation] = useState<{ lat: number; lng: number }>();
    const [showRoute, setShowRoute] = useState<{
        task: Task<IPlanPassenger<"api">>;
        vehicle: WeTrackVehicleData;
        driver: DriverType | null;
    }>();
    const [mapLoaded, setMapLoaded] = useState(false);

    const [selectedVehicle, setSelectedVehicle] = useState<WeTrackVehicleData | undefined>(undefined);
    const apiKey = import.meta.env.VITE_API_MAPS_KEY;

    const { mutateAsync: sendError } = useSendError();
    useEffect(() => {
        if (!apiKey) {
            sendError(["API Key not found"]);
        }
    }, [apiKey, sendError]);

    if (!apiKey) {
        return (
            <div className="w-full h-full flex justify-center items-center bg-white text-black">
                <h1>API Key not found :D</h1>
            </div>
        );
    }

    return (
        <context.Provider
            value={{ location, setLocation, showRoute, setShowRoute, selectedVehicle, setSelectedVehicle, mapLoaded }}
        >
            <APIProvider
                apiKey={import.meta.env.VITE_API_MAPS_KEY}
                onLoad={() => {
                    setMapLoaded(true);
                }}
            >
                {children}
            </APIProvider>
        </context.Provider>
    );
};

const useMapContext = () => {
    const ctx = useContext(context);
    if (!ctx) {
        throw new Error("useMapContext must be used within a DriversContextProvider");
    } else {
        return ctx;
    }
};

export default useMapContext;
